// Public, indexable SEO landing page for GCSE Maths revision.
// Marketing surface — no auth gate. Internal links push trial signups
// or send authenticated users into /maths.

import GCSESubjectHub from './GCSESubjectHub';

const config = {
  subjectKey: 'maths',

  path: '/gcse-maths-revision',

  appHubPath: '/maths',

  crumbLabel: 'GCSE Maths Revision',

  seoTitle: 'GCSE Maths Revision — Foundation & Higher | Grade9Ready',

  seoDescription:
    'Master GCSE Maths with AI-marked practice across Number, Algebra, Geometry, Probability, Statistics and more. Foundation and Higher tier revision with instant feedback. Try free.',

  eyebrow: 'GCSE Maths · Foundation + Higher · AI-marked practice',

  heroChip: 'bg-indigo-100 text-indigo-700 border border-indigo-200',

  h1: 'GCSE Maths revision that builds Grade 9 thinking',

  heroLede:
    'Stop guessing your revision. Grade9Ready turns every GCSE Maths topic — from fractions and percentages to quadratics, circle theorems, trigonometry and probability — into structured, adaptive practice with instant AI marking and clear next steps. Built for Foundation and Higher tier pupils.',

  coverageLede:
    'Every GCSE Maths topic is broken down into a clear learning journey: explainer, worked examples, interactive warm-up, levelled practice and an AI-marked challenge. Below is a snapshot of the seven core sections covered inside Grade9Ready.',

  coverage: [
    {
      title: 'Number',
      body:
        'Place value, factors, multiples, primes, fractions, decimals, percentages, powers, roots, indices, surds, standard form, bounds, rounding and recurring decimals.',
    },
    {
      title: 'Algebra',
      body:
        'Simplifying expressions, expanding brackets, factorising, solving equations, inequalities, sequences, simultaneous equations, quadratics, rearranging formulae and iteration.',
    },
    {
      title: 'Graphs',
      body:
        'Coordinates, straight-line graphs, gradients, intercepts, perpendicular lines, quadratic graphs, cubic graphs, reciprocal graphs, exponential graphs, transformations and circles.',
    },
    {
      title: 'Ratio, Proportion & Rates of Change',
      body:
        'Ratio notation, sharing in a ratio, direct proportion, inverse proportion, percentage change, compound interest, unit conversions, density, speed, best buys and rates.',
    },
    {
      title: 'Geometry & Measures',
      body:
        'Angles, polygons, area, perimeter, volume, surface area, Pythagoras, trigonometry, sine rule, cosine rule, bearings, constructions, transformations, vectors and circle theorems.',
    },
    {
      title: 'Probability & Statistics',
      body:
        'Probability scales, relative frequency, tree diagrams, conditional probability, Venn diagrams, averages, range, scatter graphs, cumulative frequency, histograms and box plots.',
    },
    {
      title: 'Exam Practice',
      body:
        'Section mocks, full mock exams, mixed-topic problem solving, multi-step worded questions, reasoning questions, method marks, worked solutions and instant feedback.',
    },
  ],

  tipLabels: [
    'Solving quadratics',
    'Completing the square',
    'Tree diagrams',
    'Circle theorems',
    'Trigonometry',
    'Compound interest',
  ],

  finalCtaH2: 'Start your GCSE Maths revision today',

  finalCtaLede:
    'Open your free 7-day Early Access. Get structured topic revision, levelled practice, AI marking, mock exams and parent visibility from minute one.',

  faqs: [
    {
      q: 'Is the Maths content aligned to GCSE specifications?',
      a:
        'Yes. Grade9Ready is built around the core GCSE Maths content areas used across the main English exam boards: Number, Algebra, Ratio and Proportion, Geometry and Measures, Probability and Statistics. The topic structure is suitable for pupils studying Foundation or Higher tier with AQA, Edexcel or OCR.',
    },
    {
      q: 'Does Grade9Ready support both Foundation and Higher tier Maths?',
      a:
        'Yes. Foundation content supports pupils working towards grades 1-5, while Higher content supports pupils targeting grades 4-9. Topics and practice are structured so pupils can build fluency first, then move towards harder reasoning and problem-solving questions.',
    },
    {
      q: 'How does the AI marking work?',
      a:
        'Pupils type their answer, working or explanation. The AI marker gives a score, explains what was correct, identifies missing method or accuracy marks, and gives a targeted improvement point. This helps pupils understand why marks were lost rather than just seeing the final answer.',
    },
    {
      q: 'Is there a mock exam mode?',
      a:
        'Yes. Pupils can complete section mocks and full mixed-topic mock exams. There is also a GCSE Problem Mode for longer, multi-step questions that test reasoning, interpretation and exam technique.',
    },
    {
      q: 'Can parents see progress?',
      a:
        'Yes. Parents can see topic-by-topic progress, accuracy, weaker areas and revision activity. This makes it easier to support revision without guessing what the pupil has actually practised.',
    },
    {
      q: 'How much does it cost?',
      a:
        'You can start with a free trial. Paid plans are designed to be affordable for families and give access to structured revision, practice, mock exams and progress tracking. See the pricing page for the latest plan details.',
    },
    {
      q: 'Which year groups is this for?',
      a:
        'Grade9Ready is mainly built for Year 10 and Year 11 pupils preparing for GCSE Maths, but it can also support Year 9 pupils who are starting GCSE-style content early or aiming to strengthen their foundations before Key Stage 4.',
    },
  ],
};

export default function GCSEMathsRevisionHub() {
  return <GCSESubjectHub config={config} />;
}